import {
    IonButton,
    IonCard,
    IonContent,
    IonFooter,
    IonItem,
    IonPage,
    IonText,
    useIonRouter,
} from "@ionic/react";
import { useTranslation } from "react-i18next";
import React, { FC, useState, useEffect } from "react";
import "../login/login.css";
import { routes } from "../../route/constants";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { HeaderComponent, PitchPositionSelector } from "../../components";
import {
    updateProfile,
    formatProfile,
    getProfile,
} from "../../services/profileService";
import { FootballPosition } from "../../types/footballPosition";
import {
    setLoggedInOrRegisterComplete,
    setShowOnBoardingFlow,
} from "../../redux/actions/appStatus";

/**
 * Player Position
 */
const RegisterStepFour: FC = () => {
    const { t } = useTranslation();
    const profile = useSelector((root: RootState) => root.currentUser?.profile);
    const route = useIonRouter();
    const dispatch = useDispatch();
    const [selectedPosition, setSelectedPosition] =
        useState<FootballPosition | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const userName = profile?.username;
    const [liveProfile, setLiveProfile] = useState<any>();
    useEffect(() => {
        const run = async () => {
            if (!userName) return;
            const profile = await getProfile(userName);
            console.log("Live Profile:", profile);
            setLiveProfile(profile);
        };
        run();
    }, [userName]);

    // Prepopulate position if it already exists in the profile
    useEffect(() => {
        if (liveProfile?.position) {
            setSelectedPosition(liveProfile.position as FootballPosition);
        }
    }, [liveProfile?.position]);

    const handleNext = async () => {
        if (!liveProfile) return;

        setIsLoading(true);
        try {
            const updatedProfile = {
                ...liveProfile,
                position: selectedPosition,
            };

            const formattedProfile = formatProfile(updatedProfile);
            await updateProfile(liveProfile.username, formattedProfile);

            // Complete registration and start onboarding flow
            dispatch(setLoggedInOrRegisterComplete(true));
            dispatch(setShowOnBoardingFlow(true));
            route.push(routes.onboardingGettingStarted, "forward", "replace");
        } catch (error) {
            console.error("Failed to update profile with position:", error);
            // Continue to onboarding even if update fails
            dispatch(setLoggedInOrRegisterComplete(true));
            dispatch(setShowOnBoardingFlow(true));
            route.push(routes.onboardingGettingStarted, "forward", "replace");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSkip = async () => {
        if (!liveProfile) return;

        setIsLoading(true);
        try {
            // Complete registration and start onboarding flow
            dispatch(setLoggedInOrRegisterComplete(true));
            dispatch(setShowOnBoardingFlow(true));
            route.push(routes.onboardingGettingStarted, "forward", "replace");
        } catch (error) {
            console.error(
                "Failed to update profile with null position:",
                error
            );
            // Continue to onboarding even if update fails
            dispatch(setLoggedInOrRegisterComplete(true));
            dispatch(setShowOnBoardingFlow(true));
            route.push(routes.onboardingGettingStarted, "forward", "replace");
        } finally {
            setIsLoading(false);
        }
    };

    const handlePositionChange = (position: FootballPosition | null) => {
        setSelectedPosition(position);
    };

    return (
        <IonPage>
            <HeaderComponent
                title={t("help.OnboardingStepFour.heading")}
                showProgressBar={true}
                progressValue={0.6}
            />

            <IonContent>
                <h2 className="onboarding-section-header">
                    {t("help.OnboardingStepFour.position.title")}
                </h2>
                <IonItem lines="none" color="transparent">
                    <IonText className="align-center" style={{ width: "100%" }}>
                        {t("help.OnboardingStepFour.position.subtitle")}
                    </IonText>
                </IonItem>

                <IonCard className="question-container">
                    <PitchPositionSelector
                        selectedPosition={selectedPosition}
                        onPositionChange={handlePositionChange}
                    />
                </IonCard>
            </IonContent>

            <IonFooter>
                <IonButton
                    expand="block"
                    fill="clear"
                    onClick={handleSkip}
                    disabled={isLoading}
                    data-testid="skipButton"
                >
                    {t("general.skip")}
                </IonButton>
                <IonButton
                    expand="block"
                    onClick={handleNext}
                    disabled={isLoading}
                    data-testid="nextButton"
                >
                    {t("general.next")}
                </IonButton>
            </IonFooter>
        </IonPage>
    );
};

export default RegisterStepFour;
